import axios from 'axios';
import { useState, useEffect } from 'react';
import CardTwo from '../../components/CardTwo.tsx';
import SalesCard from '../../components/SalesCard.tsx';
import PaymentCancelledCard from '../../components/PaymentCancelledCard.tsx';
import OrdersCard from '../../components/OrdersCard.tsx';
import TableOne from '../../components/TableOne.tsx';
import { getAccessToken } from '../../services/http-common.tsx';
import { BASEURL, API_SECRET, API_KEY } from '../Constants.tsx';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Link } from 'react-router-dom';
interface OrderInterface {
  orderId?: any | null;
  amount: string;
  status: string;
  date: string;
} 


dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const ECommerce = () => {

  const [currentorders, setCurrentOrders] = useState<number>(0);
  const [orders, setOrders] = useState<OrderInterface[]>([]);
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    monthlySales: 0,
    weeklySales: 0,
    dailySales: 0
  });
  const [cancelledData, setCancelledData] = useState({
    totalCancelled: 0,
    monthlyCancelled: 0,
    weeklyCancelled: 0,
    dailyCancelled: 0
  });
  const [ordersData, setOrdersData] = useState({
    totalOrders: 0,
    monthlyOrders: 0,
    weeklyOrders: 0,
    dailyOrders: 0
  });

  const accessToken = getAccessToken();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASEURL}orders/count/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
            'X-API-SECRET': `${API_SECRET}`,
            'X-API-KEY': `${API_KEY}`,
          },
          method: 'GET',
        });
        const ordersData = await response.data;
        setCurrentOrders(ordersData);

        const orderResponse = await axios.get(`${BASEURL}orders/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
            'X-API-SECRET': `${API_SECRET}`,
            'X-API-KEY': `${API_KEY}`,
          },
          method: 'GET',
        });
        const totalOrders=orderResponse.data;
        setOrders(totalOrders);
        
        // Calculate sales data
        calculateSalesData(totalOrders);
        // Calculate payment cancelled data
        calculateCancelledData(totalOrders);
        // Calculate orders data
        calculateOrdersData(totalOrders);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        return null;
      }
    };
    fetchData();
  }, []); 

  const getStartOfWeek = () => dayjs().startOf('week');
  const getEndOfWeek = () => dayjs().endOf('week');

  // Filter orders to only include those from the current week
  const ordersThisWeek = orders.filter(order => {
    const orderDate = dayjs(order.date);
    return orderDate.isSameOrAfter(getStartOfWeek()) && orderDate.isSameOrBefore(getEndOfWeek());
  });


  const countOrdersByStatus = (status:any) => {
    if(orders){
    return ordersThisWeek.filter(order => order.status === status).length;
    }
  };

  const countAllOrdersByStatus = (status:any) => {
    if(orders){
    return orders.filter(order => order.status === status).length;
    }
  };

  // Count for each status
  const pendingCount = countOrdersByStatus('ORDER_PENDING');
  const paymentCancelledCount = countAllOrdersByStatus('PAYMENT_CANCELLED');

  const calculateSalesData = (ordersData: OrderInterface[]) => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const startOfWeek = now.startOf('week');
    const startOfDay = now.startOf('day');

    let totalSales = 0;
    let monthlySales = 0;
    let weeklySales = 0;
    let dailySales = 0;

    ordersData.forEach(order => {
      const orderDate = dayjs(order.date);
      const amount = parseFloat(order.amount) || 0;
      
      // Only count confirmed and completed orders for sales
      const isValidSale = [
        'ORDER_CONFIRMED', 
        'ORDER_COMPLETED', 
        'ORDER_DELIVERED',
        'PAYMENT_DONE',
        'ORDER_SHIPPED'
      ].includes(order.status);
      
      if (isValidSale && amount > 0) {
        totalSales += amount;
        
        if (orderDate.isSameOrAfter(startOfMonth)) {
          monthlySales += amount;
        }
        
        if (orderDate.isSameOrAfter(startOfWeek)) {
          weeklySales += amount;
        }
        
        if (orderDate.isSameOrAfter(startOfDay)) {
          dailySales += amount;
        }
      }
    });

    setSalesData({
      totalSales,
      monthlySales,
      weeklySales,
      dailySales
    });
  };

  const calculateCancelledData = (ordersData: OrderInterface[]) => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const startOfWeek = now.startOf('week');
    const startOfDay = now.startOf('day');

    let totalCancelled = 0;
    let monthlyCancelled = 0;
    let weeklyCancelled = 0;
    let dailyCancelled = 0;

    ordersData.forEach(order => {
      const orderDate = dayjs(order.date);
      
      if (order.status === 'PAYMENT_CANCELLED') {
        totalCancelled++;
        
        if (orderDate.isSameOrAfter(startOfMonth)) {
          monthlyCancelled++;
        }
        
        if (orderDate.isSameOrAfter(startOfWeek)) {
          weeklyCancelled++;
        }
        
        if (orderDate.isSameOrAfter(startOfDay)) {
          dailyCancelled++;
        }
      }
    });

    setCancelledData({
      totalCancelled,
      monthlyCancelled,
      weeklyCancelled,
      dailyCancelled
    });
  };

  const calculateOrdersData = (ordersData: OrderInterface[]) => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const startOfWeek = now.startOf('week');
    const startOfDay = now.startOf('day');

    let totalOrders = 0;
    let monthlyOrders = 0;
    let weeklyOrders = 0;
    let dailyOrders = 0;

    ordersData.forEach(order => {
      const orderDate = dayjs(order.date);
      
      // Count all orders regardless of status
      totalOrders++;
      
      if (orderDate.isSameOrAfter(startOfMonth)) {
        monthlyOrders++;
      }
      
      if (orderDate.isSameOrAfter(startOfWeek)) {
        weeklyOrders++;
      }
      
      if (orderDate.isSameOrAfter(startOfDay)) {
        dailyOrders++;
      }
    });

    setOrdersData({
      totalOrders,
      monthlyOrders,
      weeklyOrders,
      dailyOrders
    });
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
      <PaymentCancelledCard cancelledData={cancelledData} />
      <Link to="/orders/pending"> <CardTwo count={pendingCount} name="Weekly Pending Orders"/></Link>
      <Link to="/orders">
        <OrdersCard ordersData={ordersData} />
      </Link>
      <Link to="/sales/metrics">
        <SalesCard salesData={salesData} />
      </Link>
      </div>

      <div className="mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6 2xl:mt-7.5 2xl:gap-7.5">
        {/* <ChartOne />
        <ChartTwo />
        <ChartThree /> */}
        {/* <MapOne /> */}
        <div className="col-span-12 xl:col-span-12">
          <TableOne />
        </div>
        {/* <ChatCard /> */}
      </div>
    </>
  );
};

export default ECommerce;

